import { useState } from 'react';
import axios from 'axios';
import { Upload, Check, AlertTriangle, X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [month, setMonth] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a PDF file'); return; }
    setUploading(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/schedule/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      setPreview(response.data);
      setEditableItems(response.data.items.map(item => ({
        ...item,
        include: item.in_master,
        final_part_id: item.matched_part_id || ''
      })));
      setMonth(response.data.month || '');
      setCustomerId(response.data.matched_customer?.customer_id || '');
      toast.success(`Parsed ${response.data.total_items} items from PDF`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse PDF');
    } finally {
      setUploading(false);
    }
  };

  const toggleItem = (idx) => {
    setEditableItems(prev => prev.map((item, i) => i === idx ? {...item, include: !item.include} : item));
  };

  const updatePartId = (idx, value) => {
    setEditableItems(prev => prev.map((item, i) => i === idx ? {...item, final_part_id: value, include: !!value} : item));
  };

  const handleConfirm = async () => {
    const itemsToSave = editableItems.filter(i => i.include && i.final_part_id);
    if (itemsToSave.length === 0) { toast.error('No items selected to save'); return; }
    if (!month) { toast.error('Please set a month'); return; }

    setSaving(true);
    try {
      const schedules = itemsToSave.map(item => ({
        part_id: item.final_part_id,
        customer_id: customerId,
        month: month,
        quantity: item.quantity
      }));

      const response = await axios.post(`${API}/schedule/confirm-upload`, { schedules }, {
        
      });
      toast.success(response.data.message);
      setPreview(null);
      setEditableItems([]);
      setFile(null);
    } catch (error) {
      toast.error('Failed to save schedules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-upload-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="schedule-upload-title">UPLOAD SCHEDULE PDF</h1>
        <p className="text-sm text-text-muted mt-2">Upload a customer PO/schedule PDF to auto-create production schedules</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white border border-border rounded-sm p-6">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Select PDF</label>
            <div className="flex gap-3">
              <Input
                data-testid="pdf-file-input"
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files[0])}
                className="rounded-sm"
              />
              <Button
                data-testid="upload-pdf-button"
                onClick={handleUpload}
                disabled={uploading || !file}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium whitespace-nowrap"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'PARSING...' : 'PARSE PDF'}
              </Button>
            </div>
          </div>
        </div>
        {uploading && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-sm text-blue-700">AI is reading your PDF and extracting schedule data. This may take 15-30 seconds...</p>
          </div>
        )}
      </div>

      {/* Preview Section */}
      {preview && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Customer</p>
              <p className="text-sm font-semibold mt-1">{preview.customer_name || 'Not detected'}</p>
              {preview.matched_customer && (
                <p className="text-xs text-emerald-600 mt-1">Matched: {preview.matched_customer.customer_name}</p>
              )}
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total Items</p>
              <p className="text-2xl font-bold font-mono mt-1">{preview.total_items}</p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Matched</p>
              <p className="text-2xl font-bold font-mono mt-1 text-emerald-600">{preview.matched_count}</p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Unmatched</p>
              <p className="text-2xl font-bold font-mono mt-1 text-red-600">{preview.unmatched_count}</p>
            </div>
          </div>

          {/* Month & Customer Override */}
          <div className="bg-white border border-border rounded-sm p-6">
            <div className="flex gap-4 items-end">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Schedule Month</label>
                <Input
                  data-testid="schedule-month-input"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="rounded-sm w-48"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Customer ID</label>
                <Input
                  data-testid="customer-id-input"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  placeholder="Customer ID"
                  className="rounded-sm"
                />
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white border border-border rounded-sm">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider">Extracted Items — Review & Confirm</h2>
              <Button
                data-testid="confirm-schedules-button"
                onClick={handleConfirm}
                disabled={saving}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm font-medium"
              >
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'SAVING...' : `CONFIRM & SAVE (${editableItems.filter(i => i.include).length})`}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full caption-bottom text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider w-12">Inc.</th>
                    <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Status</th>
                    <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">PDF Part ID</th>
                    <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Mapped To</th>
                    <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Description</th>
                    <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Total Qty</th>
                    <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Match Score</th>
                  </tr>
                </thead>
                <tbody>
                  {editableItems.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`border-b transition-colors ${!item.in_master ? 'bg-red-50/30' : item.match_type === 'fuzzy' ? 'bg-amber-50/30' : ''}`}
                      data-testid={`parsed-item-${idx}`}
                    >
                      <td className="p-4 align-middle text-center">
                        <input
                          type="checkbox"
                          checked={item.include}
                          onChange={() => toggleItem(idx)}
                          className="rounded border-gray-300"
                          data-testid={`include-checkbox-${idx}`}
                        />
                      </td>
                      <td className="p-4 align-middle">
                        {item.in_master ? (
                          item.match_type === 'exact' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                              <Check className="w-3 h-3" /> Exact
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                              <AlertTriangle className="w-3 h-3" /> Fuzzy
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                            <X className="w-3 h-3" /> Not Found
                          </span>
                        )}
                      </td>
                      <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                      <td className="p-4 align-middle">
                        <Input
                          value={item.final_part_id}
                          onChange={(e) => updatePartId(idx, e.target.value)}
                          className="rounded-sm h-8 text-xs font-mono w-40"
                          data-testid={`mapped-part-input-${idx}`}
                        />
                      </td>
                      <td className="p-4 align-middle text-xs text-text-muted">{item.description}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs font-semibold">{item.quantity?.toLocaleString()}</td>
                      <td className="p-4 align-middle text-center">
                        <span className={`font-mono text-xs font-semibold ${item.match_score >= 0.8 ? 'text-emerald-600' : item.match_score >= 0.6 ? 'text-amber-600' : 'text-red-600'}`}>
                          {Math.round(item.match_score * 100)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!preview && !uploading && (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-text-muted mb-4" />
          <p className="text-gray-700">Upload a customer PO or schedule PDF to automatically extract and map parts to your production schedule.</p>
          <p className="text-xs text-text-muted mt-2">Supports table-based PDFs with part numbers and quantities</p>
        </div>
      )}
    </div>
  );
}
